import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Set-Konfigurator: Becken plus Baelle je Farbe, als mehrere Positionen in einem Aufruf
 * von /cart/add.js. Drawer, Warenkorb-Symbol und Positionsliste werden ueber dasselbe
 * CartLinesUpdateEvent aktualisiert wie beim Batch-Add in product-form.js.
 *
 * Bedienung: Becken waehlen, je Ballfarbe ein Zaehler in Sets (angezeigt in Baellen).
 * Mit Becken ist jede Menge erlaubt (auch nur das Becken). Ohne Becken (reine Baelle-Seite)
 * gilt die Mindestmenge; darunter ist der Warenkorb-Button sichtbar gesperrt und die
 * Uebersicht sagt, wie viele Baelle fehlen.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [track]
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [prevButton]
 * @property {HTMLButtonElement} [nextButton]
 * @property {HTMLButtonElement[]} [thumbs]
 * @property {HTMLInputElement[]} [poolInputs]
 * @property {HTMLElement} [customInfo]
 * @property {HTMLElement} [totalInfo]
 * @property {HTMLElement[]} [priceCells]
 * @property {HTMLElement} [fill]
 * @property {HTMLElement} [fillBar]
 * @property {HTMLElement} [fillText]
 * @property {HTMLInputElement[]} [ballInputs]
 * @property {HTMLOutputElement[]} [ballOutputs]
 * @property {HTMLButtonElement[]} [minusButtons]
 * @property {HTMLButtonElement[]} [plusButtons]
 * @property {HTMLElement} price
 * @property {HTMLElement} summary
 * @property {HTMLElement} error
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement} [addLabel]
 * @property {HTMLElement} [stickyBar]
 * @property {HTMLElement} [stickySummary]
 * @property {HTMLElement} [stickyPrice]
 *
 * @typedef {object} ConfigState
 * @property {{ id: string, title: string, price: number, media: string } | null} pool
 * @property {{ id: string, title: string, price: number, media: string, quantity: number }[]} balls
 * @property {number} sets
 * @property {number} discount
 * @property {number} total
 *
 * @extends {Component<Refs>}
 */
class SetConfiguratorComponent extends Component {
  requiredRefs = ['price', 'summary', 'error', 'addButton'];

  /** @type {IntersectionObserver | null} */
  #stickyObserver = null;

  #busy = false;

  /** Index des sichtbaren Galerie-Slides */
  #slideIndex = 0;

  #scrollFrame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#update({ smooth: false });
    this.#setupStickyBar();
    this.refs.track?.addEventListener('scroll', this.#handleTrackScroll, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stickyObserver?.disconnect();
    this.refs.track?.removeEventListener('scroll', this.#handleTrackScroll);
    cancelAnimationFrame(this.#scrollFrame);
  }

  /* ---------- Bedienung ---------- */

  selectPool() {
    this.#changed();
  }

  /** @param {Event} event */
  increaseBalls(event) {
    this.#stepBalls(event, 1);
  }

  /** @param {Event} event */
  decreaseBalls(event) {
    this.#stepBalls(event, -1);
  }

  /** @param {Event} event */
  showMedia(event) {
    const button = /** @type {HTMLElement} */ (event.target);
    this.#goToMedia(button.dataset.mediaId ?? '', true);
  }

  previousMedia() {
    this.#goToSlide(this.#slideIndex - 1, true);
  }

  nextMedia() {
    this.#goToSlide(this.#slideIndex + 1, true);
  }

  /** @param {Event} event */
  async addToCart(event) {
    if (this.#busy) return;

    const trigger = /** @type {HTMLElement} */ (event.target);
    const goToCheckout = trigger.dataset.checkout === 'true';
    const state = this.#state();

    const message = this.#validate(state);
    if (message) {
      this.#showError(message);
      // Aus der Sticky-Leiste heraus zum Konfigurator zurueckholen, damit der Hinweis sichtbar ist.
      if (trigger.closest('.set-configurator__sticky')) {
        this.refs.error.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    /** @type {{ id: number, quantity: number }[]} */
    const items = [];
    if (state.pool) items.push({ id: Number(state.pool.id), quantity: 1 });
    for (const ball of state.balls) {
      if (ball.quantity > 0) items.push({ id: Number(ball.id), quantity: ball.quantity });
    }

    this.#clearError();
    this.#setBusy(true);

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    if (!goToCheckout) {
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
          promise: deferred.promise,
        })
      );
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();

      if (data.status) {
        this.#showError(data.description || data.message);
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        const cart = await this.#fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'set-configurator', itemCount },
        });
        return;
      }

      if (goToCheckout) {
        window.location.assign(this.dataset.checkoutUrl || '/checkout');
        return;
      }

      const cart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          sections: data.sections,
          source: 'set-configurator',
          itemCount,
        },
      });
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#setBusy(false);
    }
  }

  /* ---------- Logik ---------- */

  /** @param {ConfigState} state */
  #validate(state) {
    if (!this.#tooFew(state)) return '';
    const setSize = this.#num('setSize');
    const min = this.#num('minSets');
    return (this.dataset.customMinHint ?? '')
      .replace('[fehlen]', String((min - state.sets) * setSize))
      .replace('[min]', String(min * setSize));
  }

  /** @returns {ConfigState} */
  #state() {
    const checked = this.refs.poolInputs?.find((input) => input.checked);
    const pool =
      checked && checked.value
        ? {
            id: checked.value,
            title: checked.dataset.title ?? '',
            price: Number(checked.dataset.price),
            media: checked.dataset.mediaId ?? '',
          }
        : null;

    const balls = (this.refs.ballInputs ?? []).map((input) => ({
      id: input.dataset.variantId ?? '',
      title: input.dataset.title ?? '',
      price: Number(input.dataset.price),
      media: input.dataset.mediaId ?? '',
      quantity: Number.parseInt(input.value, 10) || 0,
    }));

    const sets = balls.reduce((sum, ball) => sum + ball.quantity, 0);
    const discount = this.#discountFor(sets);
    const ballsTotal = balls.reduce((sum, ball) => sum + Math.max(ball.price - discount, 0) * ball.quantity, 0);

    return { pool, balls, sets, discount, total: (pool?.price ?? 0) + ballsTotal };
  }

  /* ---------- Anzeige ---------- */

  #changed() {
    this.#clearError();
    this.#update({ smooth: true });
  }

  /** @param {{ smooth: boolean }} options */
  #update({ smooth }) {
    const state = this.#state();
    // Noch nichts gewaehlt (reine Baelle-Seite): Einstiegspreis der Mindestmenge statt 0,00
    const empty = state.total === 0;
    const price = empty ? this.#fromPrice() : this.#money(state.total);

    this.refs.price.textContent = price;
    if (this.refs.stickyPrice) this.refs.stickyPrice.textContent = price;
    if (this.refs.addLabel) {
      this.refs.addLabel.textContent = empty ? this.dataset.addLabel ?? '' : `${this.dataset.addLabel ?? ''} · ${price}`;
    }

    const summary = this.#summary(state);
    this.refs.summary.textContent = summary;
    if (this.refs.stickySummary) this.refs.stickySummary.textContent = summary;

    this.#renderInfo(state);
    this.#updateStepperButtons();
    this.#showRelevantMedia(state, smooth);

    // NOVELLINO: Auswahlsumme fuer den Versandstatus (snippets/versand-sync.liquid)
    this.dataset.auswahl = String(state.total);
    this.dispatchEvent(new CustomEvent('nov:auswahl', { bubbles: true }));
  }

  /** „ab 30,00 €“: Preis der Mindestmenge nach Mengenpreis */
  #fromPrice() {
    const min = this.#num('minSets');
    const base = Number(this.refs.ballInputs?.[0]?.dataset.price ?? 0);
    const amount = this.#money(Math.max(base - this.#discountFor(min), 0) * min);
    return (this.dataset.fromLabel ?? '[preis]').replace('[preis]', amount);
  }

  /** @param {ConfigState} state */
  #summary(state) {
    const parts = [];
    if (state.pool) parts.push(`${this.dataset.poolWord ?? ''} ${state.pool.title}`.trim());
    if (state.sets > 0) {
      const colors = state.balls
        .filter((ball) => ball.quantity > 0)
        .map((ball) => ball.title)
        .join(' & ');
      parts.push(`${state.sets * this.#num('setSize')} ${this.dataset.ballsWord ?? ''} ${colors}`.replace(/\s+/g, ' '));
    }
    return parts.join(' + ');
  }

  /**
   * Summe unter der Beschriftung „Baelle“; unter den Zaehlern nur der Hinweis auf fehlende Baelle.
   * Dazu wird in "Preise auf einen Blick" die gewaehlte Menge hervorgehoben.
   * @param {ConfigState} state
   */
  #renderInfo(state) {
    const tooFew = this.#tooFew(state);
    this.refs.addButton.setAttribute('aria-disabled', String(tooFew));

    for (const cell of this.refs.priceCells ?? []) {
      cell.toggleAttribute('data-active', Number(cell.dataset.sets) === state.sets);
    }

    this.#renderFill(state);

    if (this.refs.totalInfo) {
      this.refs.totalInfo.textContent =
        state.sets > 0 ? (this.dataset.customTotal ?? '').replace('[baelle]', String(state.sets * this.#num('setSize'))) : '';
    }
    if (this.refs.customInfo) this.refs.customInfo.textContent = tooFew && state.sets > 0 ? this.#validate(state) : '';
  }

  /**
   * NOVELLINO: Fuellstand-Leiste. Breite = Anteil an der groessten Menge aus "Preise auf einen Blick".
   * @param {ConfigState} state
   */
  #renderFill(state) {
    const { fill, fillBar, fillText } = this.refs;
    if (!fill || !fillBar) return;

    const max = Number(fill.dataset.max) || 1;
    fillBar.style.width = `${Math.min(state.sets / max, 1) * 100}%`;

    if (!fillText) return;
    if (state.sets === 0) {
      fillText.textContent = fill.dataset.emptyText ?? '';
      return;
    }
    const notes = Object.fromEntries(
      (fill.dataset.notes ?? '')
        .split('|')
        .filter(Boolean)
        .map((pair) => pair.split(':'))
    );
    const ballsTotal = state.total - (state.pool?.price ?? 0);
    const count = state.sets * this.#num('setSize');
    const parts = [`${count} ${this.dataset.ballsWord ?? ''}`.trim()];
    if (notes[state.sets]) parts.push(notes[state.sets]);
    parts.push(this.#money(ballsTotal));
    fillText.textContent = parts.join(' · ');
  }

  /**
   * Zu wenig Baelle: Die Mindestmenge gilt nur ohne Becken (reine Baelle-Seite).
   * Zum Becken ist jede Menge moeglich, auch 0 oder 100.
   * @param {ConfigState} state
   */
  #tooFew(state) {
    return !state.pool && state.sets < this.#num('minSets');
  }

  #updateStepperButtons() {
    // Mit Becken: hoechstens so viele Baelle, wie sinnvoll hineinpassen – ueber alle Farben
    const cap = this.#num('maxWithPool');
    const total = (this.refs.ballInputs ?? []).reduce((sum, input) => sum + (Number.parseInt(input.value, 10) || 0), 0);
    const full = cap > 0 && total >= cap;
    for (const input of this.refs.ballInputs ?? []) {
      const value = Number.parseInt(input.value, 10) || 0;
      const max = Number.parseInt(input.max, 10) || 0;
      const id = input.dataset.variantId;
      const minus = this.refs.minusButtons?.find((button) => button.dataset.variantId === id);
      const plus = this.refs.plusButtons?.find((button) => button.dataset.variantId === id);
      if (minus) minus.disabled = input.disabled || value <= 0;
      input.closest('li')?.toggleAttribute('data-active', value > 0);
      if (plus) plus.disabled = input.disabled || value >= max || full;
    }
  }

  /**
   * Springt in der Galerie zum passenden Foto der aktuellen Auswahl.
   * @param {ConfigState} state
   * @param {boolean} smooth
   */
  #showRelevantMedia(state, smooth) {
    const slides = this.refs.slides ?? [];
    const has = (/** @type {string} */ id) => Boolean(id) && slides.some((slide) => slide.dataset.mediaId === id);

    let target = '';
    if (state.pool) {
      target = has(state.pool.media) ? state.pool.media : this.dataset.poolMedia ?? '';
    } else {
      const ball = state.balls.find((entry) => entry.quantity > 0 && has(entry.media));
      target = ball ? ball.media : this.dataset.ballsMedia ?? '';
    }
    if (has(target)) this.#goToMedia(target, smooth);
  }

  /**
   * @param {string} mediaId
   * @param {boolean} smooth
   */
  #goToMedia(mediaId, smooth) {
    const index = (this.refs.slides ?? []).findIndex((slide) => slide.dataset.mediaId === mediaId);
    if (index > -1) this.#goToSlide(index, smooth);
  }

  /**
   * @param {number} index
   * @param {boolean} smooth
   */
  #goToSlide(index, smooth) {
    const track = this.refs.track;
    const count = this.refs.slides?.length ?? 0;
    if (!track || count === 0) return;
    const next = Math.min(Math.max(index, 0), count - 1);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: next * track.clientWidth, behavior: smooth && !reduceMotion ? 'smooth' : 'instant' });
    this.#setSlideIndex(next);
  }

  #handleTrackScroll = () => {
    cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = requestAnimationFrame(() => {
      const track = this.refs.track;
      if (!track || !track.clientWidth) return;
      this.#setSlideIndex(Math.round(track.scrollLeft / track.clientWidth));
    });
  };

  /** @param {number} index */
  #setSlideIndex(index) {
    const slides = this.refs.slides ?? [];
    if (index !== this.#slideIndex) {
      // Video auf dem verlassenen Slide anhalten
      slides[this.#slideIndex]?.querySelector('video')?.pause();
    }
    this.#slideIndex = index;
    const current = slides[index]?.dataset.mediaId;
    for (const thumb of this.refs.thumbs ?? []) {
      const active = thumb.dataset.mediaId === current;
      thumb.setAttribute('aria-pressed', String(active));
      // NOVELLINO: Miniaturleiste laeuft mit, damit die aktive Miniatur immer sichtbar ist
      if (active) {
        const bar = thumb.parentElement;
        if (bar && bar.scrollWidth > bar.clientWidth) {
          const left = thumb.offsetLeft;
          const target = Math.max(0, left - (bar.clientWidth - thumb.offsetWidth) / 2);
          bar.scrollTo({ left: target, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }
      }
    }
    // NOVELLINO: Beim Blaettern einen aktiven Zoom zuruecksetzen
    for (const slide of this.refs.slides ?? []) slide.removeAttribute('data-zoom');
    if (this.refs.prevButton) this.refs.prevButton.disabled = index <= 0;
    if (this.refs.nextButton) this.refs.nextButton.disabled = index >= slides.length - 1;
  }

  /* ---------- Sticky-Leiste ---------- */

  #setupStickyBar() {
    const bar = this.refs.stickyBar;
    if (!bar) return;

    // Sichtbar, sobald der Kaufbutton nach oben aus dem Bild gescrollt ist – wie in Horizons Original.
    this.#stickyObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.dataset.stuck = String(scrolledPast);
    });
    this.#stickyObserver.observe(this.refs.addButton);
  }

  /* ---------- Hilfen ---------- */

  /**
   * @param {Event} event
   * @param {number} delta
   */
  #stepBalls(event, delta) {
    const button = /** @type {HTMLElement} */ (event.target);
    const input = this.refs.ballInputs?.find((entry) => entry.dataset.variantId === button.dataset.variantId);
    if (!input) return;
    this.#setInputValue(input, (Number.parseInt(input.value, 10) || 0) + delta);
    this.#changed();
  }

  /**
   * @param {HTMLInputElement} input
   * @param {number} value
   */
  #setInputValue(input, value) {
    const max = Number.parseInt(input.max, 10) || 0;
    const sets = Math.min(Math.max(value, 0), max);
    input.value = String(sets);
    // Sichtbar in Baellen zaehlen, nicht in Sets
    const output = this.refs.ballOutputs?.find((entry) => entry.dataset.variantId === input.dataset.variantId);
    if (output) output.textContent = String(sets * this.#num('setSize'));
  }

  /** @param {number} sets */
  #discountFor(sets) {
    return this.#tiers().reduce((best, tier) => (sets >= tier.min && tier.discount > best ? tier.discount : best), 0);
  }

  /** @returns {{ min: number, discount: number }[]} */
  #tiers() {
    try {
      return JSON.parse(this.dataset.tiers || '[]')
        .filter((/** @type {{ min: number, discount: number }} */ tier) => tier.discount > 0)
        .sort((/** @type {{ min: number }} */ a, /** @type {{ min: number }} */ b) => a.min - b.min);
    } catch {
      return [];
    }
  }

  /** @param {string} key */
  #num(key) {
    return Number.parseInt(this.dataset[key] ?? '0', 10) || 0;
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'EUR');
  }

  /** @param {string} message */
  #showError(message) {
    this.refs.error.textContent = message;
  }

  #clearError() {
    this.refs.error.textContent = '';
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.refs.addButton.setAttribute('aria-busy', String(busy));
  }

  async #fetchCart() {
    /** @type {(HTMLElement & { fetchCartData?: () => Promise<any> }) | null} */
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      if (cartItems.fetchCartData) return cartItems.fetchCartData();
    }
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return response.json();
  }
}

if (!customElements.get('set-configurator-component')) {
  customElements.define('set-configurator-component', SetConfiguratorComponent);
}
